import proCardStyles from '@components/DataDisplay/ProCard/ProCard.module.scss'
import DonutCard from '@dashboard/components/DonutCard'
import type { TopCountryEntry } from '@dashboard/types/analytics.types'
import { buildTopNWithOther } from '@dashboard/utils/analyticsHelpers'
import cn from '@integrations/classnames'
import { tr } from 'mmfintech-commons'

const COUNTRY_COLORS = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--dash-text-faint)',
  'var(--dash-border-medium)'
]

function countryName(code: string): string | null {
  try {
    const name = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(code)
    return name && name !== code ? name : null
  } catch {
    return null
  }
}

interface Props {
  countries: TopCountryEntry[]
  isLoading: boolean
}

function TopCountriesCard({ countries, isLoading }: Props) {
  const segments = buildTopNWithOther(countries, 'countryCode', COUNTRY_COLORS)
  const totalCount = countries.reduce((sum, country) => sum + (country.transactionCount ?? 0), 0)

  return (
    <DonutCard isLoading={isLoading}>
      <DonutCard.Header
        title={tr('FRONTEND.DASHBOARD.TOP_COUNTRIES.TITLE', 'Top Countries')}
        subtitle={tr('FRONTEND.DASHBOARD.TOP_COUNTRIES.SUBTITLE', 'By transaction volume')}
      />
      <DonutCard.Body
        segments={segments}
        innerLabel={String(totalCount)}
        innerCaption={tr('FRONTEND.DASHBOARD.TRANSACTIONS_SHORT', 'txns')}
        emptyMessage={tr('FRONTEND.DASHBOARD.NO_TRANSACTION_DATA', 'No transaction data available.')}
      >
        {segments.length > 0
          ? segments.map((seg) => {
              const name = countryName(seg.label)
              return (
                <div key={seg.label} className={cn(proCardStyles['pro-card__legend-item'], 'pro-card__legend-item')}>
                  <div className={cn(proCardStyles['pro-card__legend-label'], 'pro-card__legend-label')}>
                    <span
                      className={cn(proCardStyles['pro-card__legend-dot'], 'pro-card__legend-dot')}
                      style={{ background: seg.color }}
                    />
                    {name ?? seg.label}
                    {name && (
                      <span className={cn(proCardStyles['pro-card__legend-code'], 'pro-card__legend-code')}>
                        {seg.label}
                      </span>
                    )}
                  </div>
                  <span className={cn(proCardStyles['pro-card__legend-pct'], 'pro-card__legend-pct')}>
                    {Math.round(seg.percentage)}%
                  </span>
                </div>
              )
            })
          : undefined}
      </DonutCard.Body>
    </DonutCard>
  )
}

export default TopCountriesCard
